import { shuffleDeterministic } from "@rmnlng/shared";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import type { CheckpointItem as CheckpointItemType } from "@/lib/scenarios";

type CheckpointItemProps = {
  item: CheckpointItemType;
  // Previously-selected answer for this item, if any. Used to
  // restore the user's pick when re-entering an item (e.g. via a
  // future "back" affordance). Not a lock — the user can change
  // their pick before pressing "Дальше".
  initialAnswer?: string;
  onSubmit: (given: string) => void;
};

/**
 * Renders one checkpoint quiz item. CLOZE/MULTIPLE_CHOICE items use a
 * shuffled MCQ widget when distractors are present; otherwise the
 * input falls back to a typed answer. TRANSFORM items add the source
 * sentence above the prompt.
 *
 * Doctrine §8.4: "no hints, no skips" during the quiz. The hint pills
 * from regular Open-mode quizzes are intentionally omitted here even
 * though the QuizItem carries them — the checkpoint is meant to test
 * recall, not lead the learner. Translation IS shown (it's not a
 * hint, it's the prompt context: "что значит эта фраза по-русски").
 *
 * No backwards navigation — once "Дальше" fires, the answer is
 * committed; the renderer handles only the current item.
 */
export function CheckpointItem({ item, initialAnswer, onSubmit }: CheckpointItemProps) {
  const t = useT();
  const [picked, setPicked] = useState<string | null>(initialAnswer ?? null);
  const [typed, setTyped] = useState(initialAnswer ?? "");

  const isMcq = item.options.length > 0;
  // Shuffle deterministically per slug so a refresh gives the same
  // option order — mirrors the existing Study mcq behavior.
  const options = useMemo(
    () => (isMcq ? shuffleDeterministic([...item.options], item.slug) : []),
    [item.options, item.slug, isMcq],
  );

  // Pick is reversible until the user commits via "Дальше". Re-tapping
  // a different option simply replaces the selection — matches the
  // expected radio-button affordance.
  const onPick = (opt: string) => setPicked(opt);

  const onCommit = () => {
    if (isMcq) {
      if (!picked) return;
      onSubmit(picked);
    } else {
      const v = typed.trim();
      if (!v) return;
      onSubmit(v);
    }
  };

  return (
    <div className="flex flex-1 flex-col gap-3 pt-3">
      <div className="flashcard flex-1 overflow-y-auto">
        {item.kind === "TRANSFORM" && item.sourceSentence && (
          <div className="rounded-[var(--r-sm)] bg-[color:var(--surface-2)] px-3 py-2">
            <div className="font-mono text-[10px] uppercase tracking-wider text-text-3">
              {t("skills.checkpoint_source_label")}
            </div>
            <div className="text-[15px] text-text-2">{item.sourceSentence}</div>
            {item.sourceTranslation && <div className="mt-0.5 text-[12px] text-text-3">{item.sourceTranslation}</div>}
          </div>
        )}

        <div className="text-[20px] leading-[1.4] text-text">{renderPrompt(item.prompt)}</div>
        {/*{item.translation && (
          <div className="font-mono text-[11px] text-text-3">
            {t("skills.checkpoint_translation_label")}: {item.translation}
          </div>
        )}*/}

        {isMcq ? (
          <div className="mt-3 flex flex-col gap-2">
            <div className="font-mono text-[11px] uppercase tracking-wider text-text-3">
              {t("skills.checkpoint_select_answer")}
            </div>
            {options.map((opt, i) => {
              const letter = String.fromCharCode(65 + i);
              const isSelected = picked === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => onPick(opt)}
                  className={cn(
                    "flex items-center gap-3 rounded-[var(--r-md)] border-[1.5px] px-3.5 py-3 text-left text-[15px] font-medium transition-colors",
                    isSelected
                      ? "border-[color:var(--scen-solid)] bg-[color:var(--scen-bg)]"
                      : "border-border bg-surface hover:bg-[color:var(--surface-2)]",
                  )}
                >
                  <span className="flex size-6 items-center justify-center rounded-full border-[1.5px] border-current text-[12px] font-semibold">
                    {letter}
                  </span>
                  <span className="flex-1">{opt}</span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="mt-3 flex flex-col gap-2">
            <div className="font-mono text-[11px] uppercase tracking-wider text-text-3">
              {t("skills.checkpoint_type_answer")}
            </div>
            <Input
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onCommit();
                }
              }}
              className="text-center font-mono text-[18px]"
            />
          </div>
        )}
      </div>
      <Button variant="scenario" size="block" onClick={onCommit} disabled={isMcq ? !picked : !typed.trim()}>
        {t("skills.session_continue")}
      </Button>
    </div>
  );
}

// Same blank renderer the mini-check uses — keeps the visual language
// consistent across all three quiz surfaces.
function renderPrompt(sentence: string): React.ReactNode {
  const parts = sentence.split(/(_{2,}|—{2,})/);
  return parts.map((p, i) =>
    /^_{2,}|^—{2,}/.test(p) ? (
      <span key={i} className="mx-1 inline-block min-w-[2em] border-b-2 border-current align-middle">
        &nbsp;
      </span>
    ) : (
      <span key={i}>{p}</span>
    ),
  );
}
